import { useQuery } from "@tanstack/react-query";
import type { Pead, PriceDaily } from "epsm-api-types/models";
import { useEffect, useState } from "react";
import { getPriceDataParams } from "@/shared/helpers/getPriceDataParams";
import config from "../../../config";
import type { ChartData, ReportCode } from "../../../types";
import { getDayRange } from "../../helpers/getDayRange";
import { getPricePointByReportCode } from "../../helpers/getPricePointByReportCode/getPricePointByReportCode";
import peadApi from "../../services/api";
import { UsePriceData } from "../../services/UsePriceData";

export function usePreparePriceForChart(
	pead: Pead | null,
	tileId: string,
	ticker: string,
) {
	const { priceResponseFields } = config;
	const dataParams = getPriceDataParams(
		ticker,
		pead?.earning_day,
		pead?.next_earning_day,
	);
	const { execute: executePrices } = UsePriceData.create().useGetPrices();
	const [priceLine, setPriceLine] = useState<ChartData>([]);

	const pricesOptions = peadApi.getQueryOptions<PriceDaily>(
		tileId,
		ticker,
		dataParams,
		executePrices,
		priceResponseFields,
	);

	const { data: price = [] } = useQuery(pricesOptions); // we get prices from ed - 10 days, to the next ed

	useEffect(() => {
		if (!pead || !price.length) return;

		const reportCode = pead.report_code as ReportCode;

		const edIndex = price.findIndex((el) =>
			pead.earning_day
				? new Date(el.date).toDateString() ===
					new Date(pead.earning_day).toDateString()
				: false,
		);

		const filteredPrice = filterPrices(price, edIndex, reportCode);
		const daysBetweenEdAndNextEd = getDayRange(pead).length - 1;

		const preparedPriceLine = preparePriceLine(
			filteredPrice,
			daysBetweenEdAndNextEd,
			pead.report_code as ReportCode,
		);

		setPriceLine(preparedPriceLine);
	}, [pead, price]);

	return { priceLine };
}

function filterPrices(
	price: PriceDaily[],
	edIndex: number,
	reportCode: ReportCode,
) {
	switch (reportCode) {
		case "AMC":
		case "DMT":
			return price.filter((_, i) => i >= edIndex);
		case "BTO":
			return price.filter((_, i) => i >= edIndex - 1);
		default:
			return price;
	}
}

function preparePriceLine(
	filteredPrice: PriceDaily[],
	daysBetweenEdAndNextEd: number,
	reportCode: ReportCode,
): ChartData {
	const preparedPriceLine: [number, number][] = [];
	const startDate = filteredPrice[0].date;

	let priceIndex = 0;
	let takeOpedPrice = reportCode === "DMT";
	let chartPoint = 0;

	for (let day = 0; day <= daysBetweenEdAndNextEd; day++) {
		const currentDate = new Date(startDate);
		currentDate.setUTCDate(currentDate.getUTCDate() + day);
		const currentDateISO = currentDate.toISOString();

		const currentPrice = filteredPrice[priceIndex];

		if (!currentPrice) continue;

		const currentPriceTime = currentPrice.date as unknown as string;
		const priceByReportCode = getPricePointByReportCode(
			currentPrice,
			reportCode,
			takeOpedPrice,
		);

		switch (reportCode) {
			case "AMC": {
				if (day === 0) {
					preparedPriceLine.push([chartPoint, +priceByReportCode || 0]);
					priceIndex++;
					takeOpedPrice = true;
					chartPoint++;
					continue;
				}

				if (day === 1) {
					preparedPriceLine.push([chartPoint, +priceByReportCode || 0]);

					if (takeOpedPrice) {
						day--;
						takeOpedPrice = false;
					} else {
						priceIndex++;
					}

					chartPoint++;
					continue;
				}

				break;
			}

			case "DMT": {
				if (day === 0) {
					preparedPriceLine.push([chartPoint, +priceByReportCode || 0]);

					if (takeOpedPrice) {
						day--;
						takeOpedPrice = false;
					} else {
						priceIndex++;
					}

					chartPoint++;
					continue;
				}
			}
		}

		if (
			currentPriceTime === currentDateISO &&
			currentPrice.adj_close !== null
		) {
			preparedPriceLine.push([chartPoint, +priceByReportCode || 0]);
			priceIndex++;
		}

		chartPoint++;
	}

	return preparedPriceLine;
}
